<template>
  <section aria-labelledby="admin-support-title">
    <div class="support-toolbar">
      <h2
        id="admin-support-title"
        class="text-h5"
      >
        Support messages
      </h2>
      <v-select
        v-model="status"
        :items="statuses"
        item-text="text"
        item-value="value"
        label="Status"
        outlined
        dense
        hide-details
        clearable
        @change="filterChanged"
      />
      <v-btn
        outlined
        :loading="loading"
        @click="load"
      >
        Refresh
      </v-btn>
    </div>
    <p class="text-caption mt-3">
      Privacy and security requests appear first. Dates use your local time.
    </p>
    <v-alert
      v-if="error"
      type="error"
      text
    >
      {{ error }}
    </v-alert>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :server-items-length="total"
      :page.sync="page"
      :items-per-page="20"
      :footer-props="{ 'items-per-page-options': [20] }"
      @update:page="load"
    >
      <template v-slot:item.created_at="{ item }">
        {{ date(item.created_at) }}
      </template>
      <template v-slot:item.status="{ item }">
        {{ statusText(item.status) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          small
          text
          color="primary"
          @click="open(item.id)"
        >
          View request
        </v-btn>
      </template>
      <template v-slot:no-data>
        No support messages found.
      </template>
    </v-data-table>
    <v-dialog
      v-model="dialog"
      max-width="760"
      :persistent="saving"
    >
      <v-card>
        <v-card-title>Support request</v-card-title>
        <v-card-text class="pt-3">
          <v-progress-linear
            v-if="detailLoading"
            indeterminate
          />
          <v-alert
            v-if="detailError"
            type="error"
            text
          >
            {{ detailError }}
          </v-alert>
          <template v-if="selected">
            <h3 class="mb-3">
              {{ selected.topic }}
            </h3>
            <p><strong>{{ selected.name }}</strong><br />{{ selected.email }}</p>
            <p class="text-caption">
              Reference: {{ selected.id }}<br />Received: {{ date(selected.created_at) }}<br />Email copy: {{ selected.email_status }}
            </p>
            <p v-if="selected.device">
              Device/browser: {{ selected.device }}
            </p>
            <div class="support-message-body">
              {{ selected.message }}
            </div>
            <v-select
              v-model="nextStatus"
              class="mt-5"
              :items="statuses"
              label="Request status"
              outlined
              :disabled="saving"
            />
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="nextStatus === selected.status"
              @click="save"
            >
              Save status
            </v-btn>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn
            text
            :disabled="saving"
            @click="dialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>
<script>
import support from '@/services/support'

export default {
  name: 'AdminSupport',
  data: () => ({
    items: [],
    total: 0,
    page: 1,
    status: null,
    loading: false,
    error: '',
    sequence: 0,
    dialog: false,
    selected: null,
    nextStatus: '',
    saving: false,
    detailLoading: false,
    detailError: '',
    statuses: [{ text: 'Open', value: 'open' }, { text: 'In progress', value: 'in_progress' }, { text: 'Resolved', value: 'resolved' }],
    headers: [
      { text: 'Received', value: 'created_at', sortable: false }, { text: 'Topic', value: 'topic', sortable: false },
      { text: 'Name', value: 'name', sortable: false }, { text: 'Email', value: 'email', sortable: false },
      { text: 'Status', value: 'status', sortable: false }, { text: 'Email copy', value: 'email_status', sortable: false },
      { text: '', value: 'actions', sortable: false },
    ],
  }),
  mounted() { this.load() },
  methods: {
    date(value) { return value ? new Date(/Z$/.test(value) ? value : `${value.replace(' ', 'T')}Z`).toLocaleString() : '' },
    statusText(value) {
      const match = this.statuses.find(item => item.value === value)

      return match ? match.text : value
    },
    filterChanged() { this.page = 1; this.load() },
    async load() {
      this.sequence += 1
      const { sequence } = this
      this.loading = true
      this.error = ''
      try {
        const result = await support.list({ page: this.page, status: this.status || undefined })
        if (sequence === this.sequence) { this.items = result.items; this.total = result.total }
      } catch (error) {
        if (sequence === this.sequence) this.error = (error.response && error.response.data && error.response.data.message) || 'Unable to load support messages.'
      } finally { if (sequence === this.sequence) this.loading = false }
    },
    async open(id) {
      this.dialog = true
      this.selected = null
      this.detailError = ''
      this.detailLoading = true
      try { this.selected = await support.detail(id); this.nextStatus = this.selected.status } catch (error) { this.detailError = (error.response && error.response.data && error.response.data.message) || 'Unable to load this request.' } finally { this.detailLoading = false }
    },
    async save() {
      this.saving = true
      this.detailError = ''
      try { this.selected = await support.update(this.selected.id, this.nextStatus); await this.load() } catch (error) { this.detailError = (error.response && error.response.data && error.response.data.message) || 'Unable to save the status.' } finally { this.saving = false }
    },
  },
}
</script>
<style scoped>
.support-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 16px; }
.support-toolbar h2 { flex: 1 1 240px; }
.support-toolbar .v-input { flex: 0 1 200px; }
.support-message-body { white-space: pre-wrap; overflow-wrap: anywhere; border: 1px solid rgba(128,128,128,.25); border-radius: 6px; padding: 16px; }
</style>
